import type { ChangeEvent } from 'react';
import { SourcesContact, StatutsPipeline, TerritoiresContact, TypesClient } from '@cockpit/shared';
import {
  LABEL_SOURCE_CONTACT,
  LABEL_STATUT_PIPELINE,
  LABEL_TERRITOIRE_CONTACT,
  LABEL_TYPE_CLIENT,
} from '../../lib/format';
import { Button } from '../ui/Button';

export interface ContactsFiltersValue {
  q: string;
  territoire: string;
  type_client: string;
  statut_pipeline: string;
  source: string;
  departement: string;
  tag: string;
  sort: string;
}

interface Props {
  value: ContactsFiltersValue;
  tags: string[];
  onChange: (v: ContactsFiltersValue) => void;
  onReset: () => void;
}

export function ContactsFilters({ value, tags, onChange, onReset }: Props): JSX.Element {
  const set =
    (key: keyof ContactsFiltersValue) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      onChange({ ...value, [key]: e.target.value });
    };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
      <input
        placeholder="Recherche nom / société / email / téléphone…"
        value={value.q}
        onChange={set('q')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:col-span-2"
      />
      <select
        value={value.territoire}
        onChange={set('territoire')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
      >
        <option value="">Tous territoires</option>
        {TerritoiresContact.map((t) => (
          <option key={t} value={t}>
            {LABEL_TERRITOIRE_CONTACT[t] ?? t}
          </option>
        ))}
      </select>
      <select
        value={value.type_client}
        onChange={set('type_client')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
      >
        <option value="">Tous types</option>
        {TypesClient.map((t) => (
          <option key={t} value={t}>
            {LABEL_TYPE_CLIENT[t] ?? t}
          </option>
        ))}
      </select>
      <select
        value={value.statut_pipeline}
        onChange={set('statut_pipeline')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
      >
        <option value="">Tous statuts</option>
        {StatutsPipeline.map((s) => (
          <option key={s} value={s}>
            {LABEL_STATUT_PIPELINE[s] ?? s}
          </option>
        ))}
      </select>
      <select
        value={value.source}
        onChange={set('source')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
      >
        <option value="">Toutes sources</option>
        {SourcesContact.map((s) => (
          <option key={s} value={s}>
            {LABEL_SOURCE_CONTACT[s] ?? s}
          </option>
        ))}
      </select>
      <input
        placeholder="Département (ex: 85)"
        value={value.departement}
        onChange={set('departement')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500"
      />
      <select
        value={value.tag}
        onChange={set('tag')}
        className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
      >
        <option value="">Tous tags</option>
        {tags.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <select
          value={value.sort}
          onChange={set('sort')}
          className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
        >
          <option value="nom_asc">Nom A→Z</option>
          <option value="nom_desc">Nom Z→A</option>
          <option value="derniere_interaction_desc">Dernière interaction ↓</option>
          <option value="created_at_desc">Plus récents</option>
        </select>
        <Button type="button" variant="secondary" size="sm" onClick={onReset}>
          Reset
        </Button>
      </div>
    </div>
  );
}
